import React, { FC, Fragment } from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { OrderForm, OrderFormFields, OrderFormTextField } from 'models/order-form';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DocumentViewer from 'components/shared/document-viewer/document-viewer.component';

type OrderFormDetailsProps = {
    orderForm: OrderForm;
    height: string;
    /** Widest the photo may be shown (CSS length) — keeps a wide scan inside the layout. */
    maxImageWidth?: string;
};
const OrderFormDetails: FC<OrderFormDetailsProps> = ({ orderForm, height, maxImageWidth }: OrderFormDetailsProps) => {
    const [showAnnotations, setShowAnnotations] = React.useState(true);

    const confidenceColor = (confidence: number | null) => {
        // Forms read by the GPT-6 reader carry no confidence: a neutral box that
        // only shows where the value was read (DD-3814).
        if (confidence === null || confidence === undefined) {
            return 'primary.main';
        }
        if (confidence > 90) {
            return 'success.main';
        }
        return confidence > 75 ? 'warning.main' : 'error.main';
    };

    const FormFieldRects: FC = () => {
        if (orderForm && orderForm.formFields) {
            const fields = Object.keys(orderForm.formFields).map(
                (k) => orderForm.formFields![k as keyof OrderFormFields],
            );
            const filtered = fields.filter(
                (field) => typeof field === 'object' && field !== null && 'value' in field,
            ) as OrderFormTextField[];

            // A field read by the GPT-6 reader can have several regions — the notes come
            // from the written text, ticks and chart marks (DD-3814). A field read by
            // Azure has only its bounds: four corners, top-left first, bottom-right third.
            const fieldRegions = (field: OrderFormTextField) => {
                if (field.regions?.length) {
                    return field.regions;
                }
                if (field.bounds?.length) {
                    const [topLeft, , bottomRight] = field.bounds;
                    return [{ x0: topLeft.x, y0: topLeft.y, x1: bottomRight.x, y1: bottomRight.y }];
                }
                return [];
            };

            const rects = filtered.flatMap((field) =>
                fieldRegions(field).map((region) => {
                    const top = region.y0 * 100;
                    const left = region.x0 * 100;
                    const width = (region.x1 - region.x0) * 100;
                    const height = (region.y1 - region.y0) * 100;
                    return {
                        top: `${top - 1}%`,
                        left: `${left - 1}%`,
                        width: `${width + 2}%`,
                        height: `${height + 2}%`,
                        // the text read at this place (one line of the notes), else the whole value
                        value: ('text' in region && region.text) || field.value,
                        confidence: field.confidence,
                    };
                }),
            );

            return (
                <Fragment>
                    {rects.map((rect, index) => (
                        <Fragment key={index}>
                            {showAnnotations && (
                                <Tooltip
                                    title={`${rect.value}`}
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: '1em',
                                                borderRadius: '5px',
                                                backgroundColor: '#333',
                                                whiteSpace: 'pre-line',
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            opacity: 0.3,
                                            position: 'absolute',
                                            top: `${rect.top}`,
                                            left: `${rect.left}`,
                                            width: `${rect.width}`,
                                            height: `${rect.height}`,
                                            border: `4px black solid`,
                                            borderColor: confidenceColor(rect.confidence),
                                            ':hover': {
                                                opacity: 1,
                                                cursor: 'help',
                                            },
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Fragment>
                    ))}
                </Fragment>
            );
        }
        return null;
    };

    const toggleAnnotaions = () => {
        setShowAnnotations(!showAnnotations);
    };

    return (
        <Fragment>
            {orderForm ? (
                <DocumentViewer
                    url={orderForm.imageUrl}
                    contentType="image/jpeg"
                    height={height}
                    testIdPrefix="order-form-details"
                    // the AI reports how far a sideways photo must turn to read upright
                    initialRotation={orderForm.imageRotation ?? 0}
                    maxImageWidth={maxImageWidth}
                    overlay={<FormFieldRects />}
                    toolbarExtra={
                        <IconButton
                            onClick={() => toggleAnnotaions()}
                            data-test="order-form-details.toggle-annotations-btn"
                        >
                            {showAnnotations ? (
                                <VisibilityOffIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                            ) : (
                                <VisibilityIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                            )}
                        </IconButton>
                    }
                />
            ) : undefined}
        </Fragment>
    );
};

export default OrderFormDetails;
